import type { DependencyObject } from '@/api/deviceApi'
import type { StreamField as ApiStreamField } from '@/api/streamsApi'
import { isNullableType, isNumericFieldType, unwrapNullable } from '@/utils/clickhouseTypes'
import { validateIdentifier } from '@/utils/normalization'
import { FIELD_TYPES } from './streamFieldTypes'

// ------------------ Types -------------------
export interface StreamField extends ApiStreamField {
	required?: boolean // UI-only field for form validation
	isDefault?: boolean // Marks if this is a default/reserved field (id, timestamp, insert_timestamp)
	isEditable?: boolean
	originalFieldName?: string // Track original name for rename detection
	originalType?: string // Track original type for allowed conversions
}

export type StreamFieldsRecord = Record<
	string,
	{
		type: string
		formatter?: string | null
		required?: boolean
		dependency?: DependencyObject
	}
>

export interface StreamData {
	name: string
	fields: Record<string, Omit<StreamField, 'name'>>
}

export interface CreateStreamFormData {
	streamName: string
	fields: StreamField[]
}

// ------------------ Default Fields -------------------
// These are the reserved fields automatically added by the backend (V3 API)
// Based on DEFAULT_FIELDS in Rust: id, timestamp, insert_timestamp
export const getDefaultFields = (): StreamField[] => [
	{
		name: 'id',
		type: 'String',
		formatter: null,
		required: true,
		isDefault: true,
		isEditable: false,
	},
	{
		name: 'timestamp',
		type: 'DateTime64(3)',
		formatter: null,
		required: true,
		isDefault: true,
		isEditable: false,
	},
	{
		name: 'insert_timestamp',
		type: 'DateTime64(3)',
		formatter: null,
		required: true,
		isDefault: true,
		isEditable: false,
	},
]

// List of default field names for quick lookup
export const DEFAULT_FIELD_NAMES = getDefaultFields().map(f => f.name)

export const isDefaultField = (fieldName: string): boolean => {
	return DEFAULT_FIELD_NAMES.includes(fieldName)
}

// Enrich fields from API response with UI-only properties (required, isDefault, isEditable)
export const enrichFieldsFromApi = (fields: ApiStreamField[]): StreamField[] => {
	return fields.map(field => ({
		...field,
		required: isDefaultField(field.name),
		isDefault: isDefaultField(field.name),
		isEditable: !isDefaultField(field.name),
	}))
}

/** Always the first two table columns, in this order, and never part of the extra-columns picker. */
export const FIXED_DISPLAY_COLUMNS = ['id', 'timestamp']

/** How many columns beyond id/timestamp a stream shows until the user picks its columns. */
export const DEFAULT_SELECTED_COLUMN_COUNT = 8

/** The picker's selection for a stream nobody has customized: the first columns alphabetically. */
export function getDefaultSelectedColumns(availableColumns: string[]): string[] {
	return [...availableColumns].sort((a, b) => a.localeCompare(b)).slice(0, DEFAULT_SELECTED_COLUMN_COUNT)
}

/** Every column a stream has besides the fixed id/timestamp ones, which the picker never lists. */
export function getExtraStreamColumns(allStreamColumns: string[]): string[] {
	return allStreamColumns.filter(col => !FIXED_DISPLAY_COLUMNS.includes(col))
}

/**
 * Resolves the columns to show from what is stored. No entry means the stream was never customized,
 * so it gets the default; an empty entry is a deliberate "id and timestamp only" and is honored.
 * Stored order wins over alphabetical, so a drag-reorder in the picker sticks. A selection whose
 * columns have all left the schema falls back to the default rather than showing nothing.
 */
export function resolveStreamDisplayColumns(storedColumns: string[] | undefined, extraColumnPool: string[]): string[] {
	if (!storedColumns) {
		return getDefaultSelectedColumns(extraColumnPool)
	}
	const pool = new Set(extraColumnPool)
	const filtered = storedColumns.filter(col => pool.has(col))
	if (storedColumns.length > 0 && filtered.length === 0) {
		return getDefaultSelectedColumns(extraColumnPool)
	}
	return filtered
}

/**
 * Table columns derived from one stream's schema and the user's extra-column selection:
 * `fetchColumns` is what gets requested from the server, fixed id/timestamp first, and
 * `fixedDisplayColumns` are the picker's always-shown pills.
 */
export function getStreamTableColumns(
	allStreamColumns: string[],
	extraDisplayColumns: string[],
	serialKeyLabel: string,
): { fetchColumns: string[]; fixedDisplayColumns: { value: string; label: string }[] } {
	const columnSet = new Set(allStreamColumns)
	const presentFixedColumns = FIXED_DISPLAY_COLUMNS.filter(col => columnSet.has(col))
	return {
		fetchColumns: [...presentFixedColumns, ...extraDisplayColumns],
		fixedDisplayColumns: presentFixedColumns.map(col => ({
			value: col,
			label: col === 'id' ? serialKeyLabel : 'Timestamp',
		})),
	}
}

/** Numeric columns are the only ones a display formatter applies to. */
export const isNumericType = isNumericFieldType

/** Backend caps device state at 128 columns, tenant-wide (create_state_key rejects once count >= 128). */
export const MAX_DEVICE_STATE_COLUMNS = 128

/** Reserved state key display names */
const RESERVED_STATE_KEY_NAMES = ['id', 'tenant_id']

/** PostgreSQL reserved keywords — backend uses display name as an unquoted column, so these return a SQL syntax error (500). */
const SQL_RESERVED_KEYWORDS = new Set([
	'all',
	'analyse',
	'analyze',
	'and',
	'any',
	'array',
	'as',
	'asc',
	'asymmetric',
	'both',
	'case',
	'cast',
	'check',
	'collate',
	'column',
	'constraint',
	'create',
	'current_catalog',
	'current_date',
	'current_role',
	'current_time',
	'current_timestamp',
	'current_user',
	'default',
	'deferrable',
	'desc',
	'distinct',
	'do',
	'else',
	'end',
	'except',
	'false',
	'fetch',
	'for',
	'foreign',
	'from',
	'grant',
	'group',
	'having',
	'in',
	'initially',
	'intersect',
	'into',
	'lateral',
	'leading',
	'limit',
	'localtime',
	'localtimestamp',
	'not',
	'null',
	'offset',
	'on',
	'only',
	'or',
	'order',
	'placing',
	'primary',
	'references',
	'returning',
	'select',
	'session_user',
	'some',
	'symmetric',
	'table',
	'then',
	'to',
	'trailing',
	'true',
	'union',
	'unique',
	'user',
	'using',
	'variadic',
	'when',
	'where',
	'window',
	'with',
])

/**
 * Validate a state key display name.
 * Must be a valid PostgreSQL identifier (lowercase letters, numbers, underscores).
 *
 * @param name - The display name to validate
 * @param existingNames - Set of existing display names to check for duplicates
 * @param currentName - Optional current name (for rename scenarios, to allow keeping the same name)
 * @param otherSelectedNames - Optional array of other names being selected (for batch validation)
 * @returns Error message string if invalid, null if valid
 */
export const validateStateKeyDisplayName = (
	name: string,
	existingNames: Set<string>,
	currentName?: string,
	otherSelectedNames?: string[],
): string | null => {
	const raw = name.trim()
	// Lowercased only for reserved/duplicate checks; format validated on `raw` to reject uppercase.
	const trimmed = raw.toLowerCase()

	// Empty is OK for new fields (will use field name as default)
	if (!raw && !currentName) {
		return null
	}

	// For rename, empty is not allowed
	if (!raw && currentName) {
		return 'Display name is required'
	}

	// Check reserved names first (before regex) for clearer error message
	if (RESERVED_STATE_KEY_NAMES.includes(trimmed) || SQL_RESERVED_KEYWORDS.has(trimmed)) {
		return `"${trimmed}" is a reserved keyword, please choose a different name`
	}

	const identifierError = validateIdentifier(raw)
	if (identifierError) {
		return identifierError
	}

	// Cannot end with "updated_at" (reserved suffix)
	if (trimmed.endsWith('updated_at')) {
		return 'Cannot end with "updated_at"'
	}

	// Check for duplicate with existing state keys (excluding current name for rename)
	if (trimmed !== currentName && existingNames.has(trimmed)) {
		return `"${trimmed}" already exists in device state`
	}

	// Check for duplicate within current batch selection
	if (otherSelectedNames?.includes(trimmed)) {
		return `"${trimmed}" is already used for another selected column`
	}

	return null
}

/**
 * Explicit type widening map matching the backend rules: widening is only allowed within
 * the same numeric family (Int -> wider Int, UInt -> wider UInt, Float32 -> Float64).
 * Cross-family conversions (signed <-> unsigned, int/uint <-> float, numeric <-> String) and
 * narrowing are never allowed — existing parquet data can't be cast like it could in ClickHouse.
 * Types without an entry (Bool, String, Date, DateTime, DateTime64(3/6)) have no widening rule
 * and fall back to keeping their existing definition (see `getAllowedTypeConversions` below).
 */
const BASE_CONVERSION_MAP: Record<string, string[]> = {
	Int8: ['Int8', 'Int16', 'Int32', 'Int64'],
	Int16: ['Int16', 'Int32', 'Int64'],
	Int32: ['Int32', 'Int64'],
	Int64: ['Int64'],
	UInt8: ['UInt8', 'UInt16', 'UInt32', 'UInt64'],
	UInt16: ['UInt16', 'UInt32', 'UInt64'],
	UInt32: ['UInt32', 'UInt64'],
	UInt64: ['UInt64'],
	Float32: ['Float32', 'Float64'],
	Float64: ['Float64'],
}

export const getAllowedTypeConversions = (currentType: string, editMode = true) => {
	if (!editMode) {
		return FIELD_TYPES
	}

	const isNullable = isNullableType(currentType)
	const baseType = unwrapNullable(currentType)

	// Types with no widening rule (Bool, String, Date, DateTime*) keep their existing definition.
	const allowedBases = BASE_CONVERSION_MAP[baseType] ?? [baseType]

	// Build the set of allowed full type values
	const allowedValues = new Set<string>()
	for (const base of allowedBases) {
		if (isNullable) {
			// Nullable can only convert to Nullable
			allowedValues.add(`Nullable(${base})`)
		} else {
			// Non-nullable can convert to both non-nullable and nullable variants
			allowedValues.add(base)
			allowedValues.add(`Nullable(${base})`)
		}
	}

	return FIELD_TYPES.filter(option => allowedValues.has(option.value))
}

/**
 * Validates if a string is a valid identifier (starts with letter/underscore,
 * contains only letters, numbers, and underscores).
 */
export const isValidIdentifier = (name: string): boolean => {
	return validateIdentifier(name) === null
}
